import React from "react";
import {
  Button, Empty, Modal, Table, Tag,
} from "antd";
import {
  EMPTY_CELL_VALUE,
  OVERDUE_ELLIPSIS_COLUMN,
  formatOverdueCellValue,
  renderOverdueEllipsisCell,
} from "./utils/overdueUtils";
import { sumColumnWidths } from "./utils/utils";
import {
  formatRcaDisplayDate,
  formatRcaDisplayDateTime,
  getRcaDocumentCount,
  enrichRcaRowWithMockFields,
  resolveRcaAssigneeDisplay,
  getRcaStatusTagColor,
} from "./utils/rcaUtils";
import { formatAddRcaTableRcaStatus } from "./utils/addRcaTestSamplesUtils";
import "../Dashboard/CustomerSubmissionDetailModal.css";
import "../Dashboard/TestMetrics.css";

const renderRcaStatusCell = (rcaStatus, record) => {
  const label = formatAddRcaTableRcaStatus(rcaStatus, record);
  if (label === "-" || label === EMPTY_CELL_VALUE) {
    return "-";
  }
  return <Tag color={getRcaStatusTagColor(rcaStatus, record)}>{label}</Tag>;
};

const renderRcaDocumentCountCell = (_value, record) => (
  formatOverdueCellValue(getRcaDocumentCount(record))
);

/**
 * Full RCA detail columns shown in the category modal (not duplicated on the main grid).
 */
const RCA_DETAIL_COLUMNS = [
  {
    title: "Customer",
    dataIndex: "customer_company",
    key: "customer_company",
    width: 160,
    ellipsis: OVERDUE_ELLIPSIS_COLUMN,
    render: renderOverdueEllipsisCell,
  },
  {
    title: "Customer ID",
    dataIndex: "customer_id",
    key: "customer_id",
    width: 120,
    render: formatOverdueCellValue,
  },
  {
    title: "WO",
    dataIndex: "workorder_id",
    key: "workorder_id",
    width: 130,
    ellipsis: OVERDUE_ELLIPSIS_COLUMN,
    render: renderOverdueEllipsisCell,
  },
  {
    title: "GBID",
    dataIndex: "genista_id",
    key: "genista_id",
    width: 140,
    ellipsis: OVERDUE_ELLIPSIS_COLUMN,
    render: renderOverdueEllipsisCell,
  },
  {
    title: "Test",
    dataIndex: "test_name",
    key: "test_name",
    width: 180,
    ellipsis: OVERDUE_ELLIPSIS_COLUMN,
    render: renderOverdueEllipsisCell,
  },
  {
    title: "Status",
    dataIndex: "rca_status",
    key: "rca_status",
    width: 110,
    render: renderRcaStatusCell,
  },
  {
    title: "Assign To",
    dataIndex: "assigned_to",
    key: "assigned_to",
    width: 150,
    ellipsis: OVERDUE_ELLIPSIS_COLUMN,
    render: (_value, record) => renderOverdueEllipsisCell(
      resolveRcaAssigneeDisplay(record),
    ),
  },
  {
    title: "Age (days)",
    dataIndex: "age_days",
    key: "age_days",
    width: 100,
    render: formatOverdueCellValue,
  },
  {
    title: "Created by",
    dataIndex: "created_by_name",
    key: "created_by_name",
    width: 140,
    ellipsis: OVERDUE_ELLIPSIS_COLUMN,
    render: renderOverdueEllipsisCell,
  },
  {
    title: "Created at",
    dataIndex: "created_at",
    key: "created_at",
    width: 170,
    ellipsis: OVERDUE_ELLIPSIS_COLUMN,
    render: (value) => renderOverdueEllipsisCell(formatRcaDisplayDateTime(value)),
  },
  {
    title: "Expected",
    dataIndex: "expected_complete_date",
    key: "expected_complete_date",
    width: 120,
    render: formatRcaDisplayDate,
  },
  {
    title: "Completed",
    dataIndex: "actual_complete_date",
    key: "actual_complete_date",
    width: 120,
    render: formatRcaDisplayDate,
  },
  {
    title: "Docs",
    key: "documentCount",
    width: 70,
    render: renderRcaDocumentCountCell,
  },
  {
    title: "Retest reason",
    dataIndex: "retest_reason",
    key: "retest_reason",
    width: 160,
    ellipsis: OVERDUE_ELLIPSIS_COLUMN,
    render: renderOverdueEllipsisCell,
  },
];

const RCA_DETAIL_TABLE_SCROLL_X = sumColumnWidths(RCA_DETAIL_COLUMNS);

/**
 * Attach parent category onto each RCA for export / display consistency.
 * @param {object} group
 * @returns {object[]}
 */
const buildCategoryModalRows = (group) => {
  if (!group) {
    return [];
  }
  const rcaList = Array.isArray(group.rcaList) ? group.rcaList : [];
  return rcaList.map((item) => enrichRcaRowWithMockFields({
    ...item,
    categoryName: group.categoryName || item.categoryName,
  }));
};

/**
 * Category row click → RCA list modal (replaces expand-in-table).
 */
export default function RcaCategoryModal({ open, group, onClose }) {
  const rows = buildCategoryModalRows(group);
  const categoryLabel = group?.categoryName || "Category";
  const subtitle = group
    ? `${group.activeCount ?? 0} active · ${group.completedCount ?? 0} completed · ${group.notStartedCount ?? 0} not started · ${group.inProgressCount ?? 0} in progress · ${group.onHoldCount ?? 0} on-hold · ${rows.length} RCAs`
    : "";

  return (
    <Modal
      className="Sample__Modal DataOpsAnalytics__DrillDownModal"
      centered
      open={open}
      title={(
        <div className="DataOpsAnalytics__DrillDownHeader">
          <div className="DataOpsAnalytics__DrillDownHeaderText">
            <div className="DataOpsAnalytics__DrillDownTitle">{categoryLabel}</div>
            {subtitle ? (
              <div className="DataOpsAnalytics__DrillDownSubtitle">{subtitle}</div>
            ) : null}
          </div>
        </div>
      )}
      onCancel={onClose}
      footer={[
        <Button key="close" type="primary" onClick={onClose}>
          Close
        </Button>,
      ]}
      width="80vw"
      destroyOnClose
    >
      <Table
        bordered
        size="middle"
        className="DataOpsAnalytics__Table"
        rowKey="rowKey"
        columns={RCA_DETAIL_COLUMNS}
        dataSource={rows}
        pagination={false}
        scroll={{ x: RCA_DETAIL_TABLE_SCROLL_X, y: "50vh" }}
        locale={{
          emptyText: (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data" />
          ),
        }}
      />
    </Modal>
  );
}
